import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import NexusHeader from '../components/NexusHeader';
import { ArrowLeft, Info, Save, RotateCcw } from 'lucide-react';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

const DEFAULTS = { min_pass_score: 8.0, max_attempts_before_rescue: 3, adjusted_score_on_rescue: 8.2 };

const SOASettingsPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [settings, setSettings] = useState(DEFAULTS);
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // {type, text}

  const headers = {
    'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    'Content-Type': 'application/json',
  };

  useEffect(() => {
    if (!isAdmin()) navigate('/dashboard');
  }, [isAdmin, navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin-panel/soa-settings`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        setForm({
          min_pass_score: data.min_pass_score ?? DEFAULTS.min_pass_score,
          max_attempts_before_rescue: data.max_attempts_before_rescue ?? DEFAULTS.max_attempts_before_rescue,
          adjusted_score_on_rescue: data.adjusted_score_on_rescue ?? DEFAULTS.adjusted_score_on_rescue,
        });
      }
    } catch (e) {
      console.error('[SOA Settings] fetch failed:', e);
      setMsg({ type: 'error', text: 'Gagal memuatkan tetapan. Cuba lagi.' });
    }
    setLoading(false);
  };

  useEffect(() => { fetchSettings(); /* eslint-disable-next-line */ }, []);

  const validate = () => {
    const errs = [];
    const score = Number(form.min_pass_score);
    const attempts = Number(form.max_attempts_before_rescue);
    const adj = Number(form.adjusted_score_on_rescue);
    if (isNaN(score) || score < 0 || score > 10) errs.push('Skor lulus mesti antara 0 dan 10');
    if (!Number.isInteger(attempts) || attempts < 1 || attempts > 10) errs.push('Jumlah cubaan mesti integer 1 hingga 10');
    if (isNaN(adj) || adj < 0 || adj > 10) errs.push('Skor pelarasan AI Rescue mesti antara 0 dan 10');
    return errs;
  };

  const onSave = async () => {
    setMsg(null);
    const errs = validate();
    if (errs.length) { setMsg({ type: 'error', text: errs.join(' • ') }); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin-panel/soa-settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          min_pass_score: Number(form.min_pass_score),
          max_attempts_before_rescue: Number(form.max_attempts_before_rescue),
          adjusted_score_on_rescue: Number(form.adjusted_score_on_rescue),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: 'success', text: 'Tetapan berjaya dikemaskini.' });
        await fetchSettings();
      } else {
        setMsg({ type: 'error', text: data.detail || 'Gagal menyimpan tetapan' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: 'Rangkaian gagal. Cuba lagi.' });
    }
    setSaving(false);
  };

  const onReset = () => {
    setForm({ ...DEFAULTS });
    setMsg({ type: 'info', text: 'Nilai dipulihkan kepada default. Klik Simpan untuk gunakan.' });
  };

  const field = (label, helper, key, { type = 'number', step = 0.1, min = 0, max = 10, testId } = {}) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 4 }}>{label}</label>
      <input
        data-testid={testId}
        type={type}
        step={step}
        min={min}
        max={max}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 8,
          border: '1px solid #D0D7DE', fontSize: 14, color: '#111', background: '#fff',
          outline: 'none',
        }}
      />
      <div style={{ fontSize: 11, color: '#666', marginTop: 4, display: 'flex', gap: 4 }}>
        <Info size={12} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>{helper}</span>
      </div>
    </div>
  );

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#F5F7FA' }}>
      <NexusHeader />

      <div style={{ background: '#0e67a2', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button data-testid="soa-settings-back-btn" onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ArrowLeft size={22} color="white" />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#fff' }}>SOA Drill Settings</h1>
          <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>Konfigur ambang skor & jumlah cubaan</p>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 100px' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Memuatkan...</div>
        ) : (
          <>
            {/* Current values readout */}
            <Card className="border-0" style={{ borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 12 }}>
              <CardContent className="p-3">
                <div style={{ fontSize: 11, color: '#999', marginBottom: 6, fontWeight: 600 }}>NILAI SEMASA</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <div style={{ textAlign: 'center', padding: '8px', background: '#F7FAFC', borderRadius: 8 }}>
                    <div data-testid="current-min-pass" style={{ fontSize: 22, fontWeight: 800, color: '#0e67a2' }}>{Number(settings.min_pass_score).toFixed(1)}</div>
                    <div style={{ fontSize: 10, color: '#666' }}>Skor Lulus</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '8px', background: '#F7FAFC', borderRadius: 8 }}>
                    <div data-testid="current-max-attempts" style={{ fontSize: 22, fontWeight: 800, color: '#F7941E' }}>{settings.max_attempts_before_rescue}</div>
                    <div style={{ fontSize: 10, color: '#666' }}>Cubaan Max</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '8px', background: '#F7FAFC', borderRadius: 8 }}>
                    <div data-testid="current-rescue-score" style={{ fontSize: 22, fontWeight: 800, color: '#4CAF50' }}>{Number(settings.adjusted_score_on_rescue).toFixed(1)}</div>
                    <div style={{ fontSize: 10, color: '#666' }}>Skor AI Rescue</div>
                  </div>
                </div>
                {settings.updated_at && (
                  <div style={{ fontSize: 10, color: '#999', marginTop: 8, textAlign: 'center' }}>
                    Dikemas kini: {new Date(settings.updated_at).toLocaleString()} oleh {settings.updated_by || 'system'}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Edit form */}
            <Card className="border-0" style={{ borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent className="p-4">
                <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 12 }}>Edit Tetapan</div>

                {field(
                  'Skor Lulus Minimum (min_pass_score)',
                  'Skor ambang minimum yang pengguna perlu capai untuk teruskan tanpa refine. Contoh: 8.0 = perlu skor ≥ 8 untuk lulus.',
                  'min_pass_score',
                  { testId: 'input-min-pass-score', step: 0.1, min: 0, max: 10 }
                )}

                {field(
                  'Cubaan Maksimum Sebelum AI Rescue',
                  'Bilangan kali pengguna boleh cuba soalan yang sama sebelum AI Rescue dicetuskan. Contoh: 3 = selepas cubaan ke-3 dan skor masih < skor lulus, AI Rescue akan muncul.',
                  'max_attempts_before_rescue',
                  { testId: 'input-max-attempts', type: 'number', step: 1, min: 1, max: 10 }
                )}

                {field(
                  'Skor Pelarasan AI Rescue (adjusted_score_on_rescue)',
                  'Skor yang akan diberikan apabila pengguna terima cadangan AI Rescue. Biasanya sedikit di atas skor lulus (contoh: 8.2).',
                  'adjusted_score_on_rescue',
                  { testId: 'input-adjusted-score', step: 0.1, min: 0, max: 10 }
                )}

                {msg && (
                  <div data-testid="settings-msg" style={{
                    padding: '8px 12px', borderRadius: 8, fontSize: 12, marginTop: 8, marginBottom: 12,
                    background: msg.type === 'success' ? '#E8F5E9' : msg.type === 'error' ? '#FFEBEE' : '#FFF8E1',
                    color: msg.type === 'success' ? '#1B5E20' : msg.type === 'error' ? '#B71C1C' : '#F57F17',
                    border: `1px solid ${msg.type === 'success' ? '#4CAF50' : msg.type === 'error' ? '#E53935' : '#F7941E'}`,
                  }}>
                    {msg.text}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button
                    data-testid="save-soa-settings-btn"
                    onClick={onSave}
                    disabled={saving}
                    style={{
                      flex: 1, padding: '12px', background: saving ? '#999' : '#0e67a2', color: '#fff',
                      border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
                      cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    <Save size={16} />
                    {saving ? 'Menyimpan...' : 'Simpan'}
                  </button>
                  <button
                    data-testid="reset-soa-settings-btn"
                    onClick={onReset}
                    disabled={saving}
                    style={{
                      padding: '12px 16px', background: '#fff', color: '#0e67a2',
                      border: '1px solid #0e67a2', borderRadius: 8, fontSize: 14, fontWeight: 700,
                      cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <RotateCcw size={16} />
                    Default
                  </button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default SOASettingsPage;
